import { useEffect, useRef, useState } from "react";
import { animate, useInView, motion, useScroll, useTransform } from "framer-motion";
import { Camera } from "lucide-react";
import { BRAND, STATS, TEAM } from "../../content/site";
import { Reveal, SectionHeading } from "./Motion";
import { Floral } from "./Floral";

const Counter = ({ value, suffix, label, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [n, setN] = useState(value);
  useEffect(() => {
    if (!inView) return undefined;
    const c = animate(0, value, { duration: 2.2, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView, value]);
  return (
    <div ref={ref} className="border-t border-gold/30 pt-5" data-testid={testid}>
      <p className="font-serif text-6xl leading-none text-gold sm:text-7xl">
        {n}
        <span className="text-4xl sm:text-5xl">{suffix}</span>
      </p>
      <p className="mt-3 text-xs uppercase tracking-[0.25em] text-ink-soft">{label}</p>
    </div>
  );
};

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="about" ref={ref} className="relative overflow-hidden section-pad" data-testid="about-section">
      <Floral pos="tr" />
      <div className="relative z-10 mx-auto grid max-w-[1300px] items-center gap-16 lg:grid-cols-12">
        <Reveal className="relative lg:col-span-5">
          <div className="relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-t-[999px] rounded-b-2xl shadow-[0_40px_80px_-40px_rgba(74,74,66,0.5)]">
            <motion.img src="/images/w7.jpg" alt="Couple portrait" loading="lazy" style={{ y }} className="h-[118%] w-full -translate-y-[8%] object-cover" />
          </div>
          <div className="absolute -bottom-6 right-0 rounded-2xl border border-gold/30 bg-cream px-5 py-4 shadow-lg sm:right-6" data-testid="about-since-badge">
            <p className="font-script text-3xl text-gold">Since 1990</p>
            <p className="text-[11px] uppercase tracking-[0.25em] text-ink-soft">Junagadh · Ahmedabad</p>
          </div>
        </Reveal>
        <div className="lg:col-span-7 lg:pl-8">
          <SectionHeading eyebrow="Our Story" title="Three decades of" script="real emotions" testid="about-heading" />
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink" data-testid="about-text">{BRAND.about}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <blockquote className="mt-8 border-l-2 border-peach pl-5 font-serif text-xl italic leading-snug text-ink sm:text-2xl" data-testid="about-mission">
              “{BRAND.mission}”
            </blockquote>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-6 sm:gap-10">
            {STATS.map((s, i) => (
              <Counter key={s.label} {...s} testid={`about-counter-${i}`} />
            ))}
          </div>
          <Reveal delay={0.1} className="mt-12">
            <p className="eyebrow mb-4">Our team of professionals</p>
            <div className="flex flex-wrap gap-2.5" data-testid="about-team">
              {TEAM.map((t) => (
                <span key={t} className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-white/70 px-4 py-2 text-[13px] text-ink">
                  <Camera className="h-3.5 w-3.5 text-gold" />
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-6 text-sm text-ink-soft">
              Led by <span className="font-medium text-ink">{BRAND.contactPerson}</span>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
